import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { System } from '../../System';
import * as _ from 'lodash';

/**
 * This is a multi-select drop-down component with an "all" checkbox at the top
 *
 * @input label: an optional label for this component. Defaults to null.
 * @input dataSource: a list of object that binds to this drop-down to populate all the items.
 * @input displayField: gets or sets the field of the data source that provides the text content of the list items.
 * @input valueField: gets or sets the field of the data source that provides the value of each list item.
 * @input valueFieldType: Gets or sets the type of value field comparison.
 * @input selected: gets the array of checked item's value , or selects the items in the list control that contains the specified value.
 * @input selecteByCommaString(optional): gets the comma separated value of checked items, or selects the items in the list control that contains the specified value.
 * @Input selectWidth: the width of the select drop down. Defaults to 100%.
 * @Input hint: hint text to pass to Tyler Form Field
 * @Input errorMessage: error message to pass to TylerFormField
 * @Input required: flag for required fields
 * @Input disabled: determines disabled state
 * @Input moveSelectedToTop: if true, selected items in list will move to the top of the list
 * @output selectedChange: emits when an item's checkbox is checked/unchecked
 */

@Component({
    selector: 'tyl-multiple-select-list',
    templateUrl: './tyl-multiple-select-list.component.html',
    styleUrls: ['./tyl-multiple-select-list.component.css']
})
export class TylMultipleSelectListComponent implements OnInit {

    constructor() { }

    @Input() label: string;
    @Input() displayField: string;
    @Input() valueField: string;
    @Input() valueFieldType: string;
    @Input() selectWidth = '100%';
    @Input() hint: string;
    @Input() hintAlign: string;
    @Input() errorMessage: string;
    @Input() required: boolean;
    @Input() disabled: boolean;
    @Input() moveSelectedToTop = true;
    @Input() disableOptionCentering = true;
    @Input() hideSelectAll = false;
    @Input() tabIndex = 0;
    @Output() selectedChange = new EventEmitter<any>();
    @Output() openedChange = new EventEmitter<any>();
    @Output() controlEmitter = new EventEmitter<ElementRef>();

    private _matSelect: ElementRef;
    get matSelect() {
        return this._matSelect;
    }
    @ViewChild('matSelect', { read: ElementRef }) set matSelect(value: ElementRef) {
        this._matSelect = value;
        this.controlEmitter.emit(value);
    }

    dataSourceIds: any;
    private originalDataSource: any[];
    sortedDataSource: any[];
    dropdownIsOpen = false;

    _selected: any[];

    get selected() {
        const isAllItemsSelected: boolean = System.arrayEqual(this._selected, this.originalDataSource.map(x => this.getValue(x)));
        return isAllItemsSelected ? 'all' : this._selected;
    }

    @Input() set selected(value: any) {
        setTimeout(() => {
            if (value === 'all' || value[0] === 'all') {
                this._selected = this.originalDataSource.map(x => this.getValue(x));
            } else {
                this._selected = value;
            }
            // this.selectedChange.emit(this.selected);
            if (!this.dropdownIsOpen) {
                this.sortSelectedItems();
            }
        }, 0);
    }

    get selecteByCommaString() {
        return this._selected.join(',');
    }

    @Input() set selecteByCommaString(value: string) {
        if (value === 'all' || value[0] === 'all') {
            this.selected = value;
        } else {
            switch (this.valueFieldType) {
                case 'number':
                    this._selected = value.split(',').map<number>(x => Number(x));
                    break;
                case 'date':
                    this._selected = value.split(',').map<Date>(x => new Date(x));
                    break;
                default:
                    this._selected = value.split(',').map(x => x);
                    break;
            }
            // this.selectedChange.emit(this._selected);
        }
    }

    @Input() set dataSource(value: any[]) {
        this.originalDataSource = value;
        this.dataSourceIds = this.originalDataSource.map(x => this.getValue(x));
        if (!this.moveSelectedToTop) {
            this.sortedDataSource = _.cloneDeep(this.originalDataSource);
        }
        else {
            setTimeout(() => {
                this.sortSelectedItems();
            }, 0);
        }
    }

    get dataSource() {
        return this.originalDataSource;
    }

    getValue(item: any) {
        return this.valueField ? item[this.valueField] : item;
    }

    getDisplay(item: any) {
        return this.displayField ? item[this.displayField] : item;
    }

    ngOnInit() {
        this.sortSelectedItems();
    }

    // Methods
    trackByIndex(index: number, item: any) {
        // POST: resolves a bug with NgFor on primitive lists because angular
        return item[this.valueField];
    }

    sortSelectedItems() {
        if (this.moveSelectedToTop) {
            if (this.originalDataSource && this._selected) {
                this.sortedDataSource = _.cloneDeep(this.originalDataSource);
                let i = this.sortedDataSource.length;
                const selectedItems = [];
                while (i--) {
                    if (this._selected.includes(this.getValue(this.sortedDataSource[i]))) {
                        selectedItems.push(this.sortedDataSource[i]);
                        this.sortedDataSource.splice(i, 1);
                    }
                }
                selectedItems.forEach(item => {
                    this.sortedDataSource.splice(0, 0, item);
                });
            }
        }
    }

    openChange(isOpen) {
        if (!isOpen) {
            this.sortSelectedItems();
        }
        this.dropdownIsOpen = isOpen;
        this.openedChange.emit(isOpen);
    }

    focus() {
        // POST: focuses on this control
        this.matSelect.nativeElement.focus();
    }
}
